(() => {
  const FILTER_PREFIX = "filter.";
  const MANAGED_KEYS = new Set(["browse", "sort_by", "page"]);
  const SETTING_KEYS = [
    "pdp_grid",
    "pdp_grid_mobile",
    "pdp_per_page",
    "pdp_filter_layout",
    "pdp_enable_sort",
    "pdp_enable_filters",
  ];

  const isManagedParam = (key) =>
    MANAGED_KEYS.has(key) || key.startsWith(FILTER_PREFIX);
  const AB_CLASS = "ab-shop-experience-v2";
  const isAbActive = () =>
    document.documentElement.classList.contains(AB_CLASS);
  // The tabs built from the product take over only when this class is set next to AB_CLASS.
  const CONTEXT_CLASS = "ab-shop-context-v1";
  const isContextActive = () =>
    isAbActive() && document.documentElement.classList.contains(CONTEXT_CLASS);
  // Below this many products in the brand and the picked colour, the first results show the whole brand.
  const DEFAULT_COLOR_MIN_RESULTS = 4;
  const COLOR_FILTER = "filter.v.option.color";

  const isMobileView = () => {
    const small =
      window.theme && window.theme.sizes ? window.theme.sizes.small : 750;
    return window.innerWidth < small;
  };

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  class PdpCollectionBrowser extends HTMLElement {
    constructor() {
      super();
      this.config = null;
      this.panel = null;
      this.statusEl = null;
      this.tablist = null;
      this.abortController = null;
      this.requestId = 0;
      this.activeHandle = "";
      this.onPopState = this.handlePopState.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onInput = debounce(this.handleInput.bind(this), 500);
      this.onRangeUpdate = this.handleRangeUpdate.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onTabPointerDown = this.handleTabPointerDown.bind(this);
      this.onTabPointerMove = this.handleTabPointerMove.bind(this);
      this.onTabPointerUp = this.handleTabPointerUp.bind(this);
      this.abObserver = null;
      this.defaultColor = null;
      this.tabDrag = null;
      this.suppressTabClick = false;
      this.initialized = false;
    }

    connectedCallback() {
      const configEl = this.querySelector("[data-pdp-browse-config]");
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        console.error("[pdp-collection-browser] invalid config", error);
        return;
      }

      this.panel = this.querySelector("[data-pdp-browse-panel]");
      this.statusEl = this.querySelector("[data-pdp-browse-status]");
      this.tablist = this.querySelector("[data-pdp-browse-tabs]");

      if (isAbActive()) {
        this.activate();
        return;
      }

      this.abObserver = new MutationObserver(() => {
        if (isAbActive()) {
          this.disconnectAbObserver();
          this.activate();
        }
      });
      this.abObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    activate() {
      if (this.initialized) return;
      this.initialized = true;
      this.applyContext();
      this.watchSimilar();
      this.watchColor();
      this.activeHandle = this.resolveActiveHandle(
        new URL(window.location.href),
      );

      this.addEventListener("click", this.onClick);
      this.addEventListener("keydown", this.onKeyDown);
      this.addEventListener("submit", this.onSubmit);
      this.addEventListener("input", this.onInput);
      this.addEventListener("change", this.onChange);
      this.addEventListener("theme:range:update", this.onRangeUpdate);
      window.addEventListener("popstate", this.onPopState);
      if (this.tablist) {
        this.tablist.addEventListener("pointerdown", this.onTabPointerDown);
        this.tablist.addEventListener("pointermove", this.onTabPointerMove);
        this.tablist.addEventListener("pointerup", this.onTabPointerUp);
        this.tablist.addEventListener("pointercancel", this.onTabPointerUp);
      }

      this.syncTabs(this.activeHandle);
      const current = new URL(window.location.href);
      const synced = this.productUrl(
        this.activeHandle,
        this.initialParams(current),
      );
      if (
        synced.pathname + synced.search !==
        current.pathname + current.search
      ) {
        history.replaceState({}, "", synced.pathname + synced.search);
      }
      this.loadPanel({ replace: true, preserveScroll: true });
      this.watchContext();
    }

    disconnectAbObserver() {
      if (!this.abObserver) return;
      this.abObserver.disconnect();
      this.abObserver = null;
    }

    applyContext() {
      const tabs = this.config.contextTabs || [];
      if (this.contextApplied || tabs.length < 2 || !isContextActive())
        return false;
      const tablist = this.querySelector("[data-pdp-browse-context-tabs]");
      if (!tablist) return false;

      this.contextApplied = true;
      this.config.tabs = tabs;
      this.config.activeHandle =
        this.config.contextActiveHandle || tabs[0].handle;
      this.tablist = tablist;
      this.classList.add("is-context");
      return true;
    }

    // CONTEXT_CLASS can arrive after the first set of tabs is already running.
    watchContext() {
      const tabs = this.config.contextTabs || [];
      if (this.contextApplied || tabs.length < 2) return;

      this.contextObserver = new MutationObserver(() => {
        if (!isContextActive()) return;
        this.disconnectContextObserver();
        this.switchToContext();
      });
      this.contextObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    // Similar products beside the photos load once CONTEXT_CLASS is on, whatever the tabs do.
    watchSimilar() {
      if (!this.config.similar || this.similarStarted) return;
      if (isContextActive()) {
        this.loadSimilar();
        return;
      }

      this.similarObserver = new MutationObserver(() => {
        if (!isContextActive()) return;
        this.similarObserver.disconnect();
        this.similarObserver = null;
        this.loadSimilar();
      });
      this.similarObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    // Puts up to 3 best sellers of the same category and material in place of the recommended products.
    // Anything short of that (no products, slow or failed request) brings the recommended products back.
    async loadSimilar() {
      if (this.similarStarted) return;
      this.similarStarted = true;
      const aside = document.querySelector("[data-pdp-shop-aside]");
      if (!aside) return;

      const { url, sectionId, productId, title } = this.config.similar;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8_000);

      try {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set("section_id", sectionId);
        requestUrl.searchParams.set("sort_by", "best-selling");
        const response = await fetch(requestUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const doc = new DOMParser().parseFromString(
          await response.text(),
          "text/html",
        );
        const block = doc.querySelector("[data-pdp-similar-products]");
        if (!block) throw new Error("similar products missing");

        for (const item of block.querySelectorAll("quick-add-product")) {
          const holder = item.querySelector("[data-quick-add-holder]");
          if (holder?.dataset.quickAddHolder === String(productId)) item.remove();
        }
        const items = [...block.querySelectorAll("quick-add-product")];
        items.slice(3).forEach((item) => item.remove());
        if (!items.length) {
          aside.classList.add("is-similar-fallback");
          return;
        }

        const heading = block.querySelector("[data-pdp-similar-title]");
        if (heading) heading.textContent = title;
        aside.prepend(document.importNode(block, true));
        aside.classList.add("is-similar");
        aside.setAttribute("aria-label", title);
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.warn("[pdp-collection-browser] similar products", error);
        }
        aside.classList.add("is-similar-fallback");
      } finally {
        clearTimeout(timer);
      }
    }

    // The colour tab follows the colour picked on the product. Quick add windows on the page fire the same event for other products.
    watchColor() {
      const color = this.config.contextColor;
      if (!color) return;
      const variantIds = new Set(color.variantIds.map(String));
      this.onVariantChange = (event) => {
        const variant = event.detail?.variant;
        if (!variant || !variantIds.has(String(variant.id))) return;
        this.updateColorTab(variant.options?.[color.position - 1]);
      };
      document.addEventListener("theme:variant:change", this.onVariantChange);
    }

    updateColorTab(value) {
      const tab = (this.config.contextTabs || []).find(
        (item) => item.handle === "ctx-color",
      );
      const param = tab?.params?.[0];
      if (!value || !param || param[1] === value) return;
      const previous = param[1];
      param[1] = value;

      const link = this.querySelector(
        '[data-pdp-browse-context-tabs] [data-browse-handle="ctx-color"]',
      );
      if (link) {
        const href = new URL(link.href);
        href.searchParams.set(param[0], value);
        link.setAttribute("href", href.pathname + href.search);
      }

      if (this.followDefaultColor(param[0], previous, value)) return;
      if (!this.contextApplied || this.activeHandle !== "ctx-color") return;
      const url = this.productUrl(
        this.activeHandle,
        this.tabParams(this.activeHandle),
      );
      history.replaceState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true, preserveScroll: true });
    }

    // The first results keep matching the colour when the shopper picks another one.
    followDefaultColor(key, previous, value) {
      const current = this.defaultColor;
      if (!current || current.key !== key || current.value !== previous) return false;
      const url = new URL(window.location.href);
      const values = url.searchParams.getAll(key);
      if (this.activeHandle !== current.handle || !values.includes(previous)) return false;
      current.value = value;
      url.searchParams.delete(key);
      values.forEach((item) => url.searchParams.append(key, item === previous ? value : item));
      history.replaceState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true, preserveScroll: true });
      return true;
    }

    disconnectContextObserver() {
      if (!this.contextObserver) return;
      this.contextObserver.disconnect();
      this.contextObserver = null;
    }

    switchToContext() {
      const previous = this.tablist;
      if (!this.applyContext()) return;

      this.toggleTablistDrag(previous, "removeEventListener");
      this.toggleTablistDrag(this.tablist, "addEventListener");
      this.activeHandle = this.config.activeHandle;
      this.syncTabs(this.activeHandle);
      const url = this.productUrl(
        this.activeHandle,
        this.withDefaultColor(this.activeHandle, this.tabParams(this.activeHandle)),
      );
      history.replaceState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true, preserveScroll: true });
    }

    toggleTablistDrag(tablist, method) {
      if (!tablist) return;
      tablist[method]("pointerdown", this.onTabPointerDown);
      tablist[method]("pointermove", this.onTabPointerMove);
      tablist[method]("pointerup", this.onTabPointerUp);
      tablist[method]("pointercancel", this.onTabPointerUp);
    }

    // Filters a tab switches on when it opens. Only the tabs built from the product carry any.
    tabParams(handle) {
      const tab = this.config.tabs.find((item) => item.handle === handle);
      if (!tab || !Array.isArray(tab.params) || !tab.params.length) return null;
      const params = new URLSearchParams();
      tab.params.forEach(([key, value]) => params.append(key, value));
      return params;
    }

    initialParams(url) {
      const params = this.paramsFromProductUrl(url);
      if (!this.contextApplied) return params;
      if (url.searchParams.get("browse") === this.activeHandle) {
        this.restoreDefaultColor(url);
        return params;
      }
      return (
        this.withDefaultColor(this.activeHandle, this.tabParams(this.activeHandle)) ||
        new URLSearchParams()
      );
    }

    // The first results are the product's brand in the colour picked on the product, the pairing shoppers clicked on Google.
    withDefaultColor(handle, params) {
      const color = this.colorParam();
      if (!this.contextApplied || !params || !color || handle !== "ctx-brand")
        return params;
      params.append(color[0], color[1]);
      this.defaultColor = { handle, key: color[0], value: color[1] };
      return params;
    }

    colorParam() {
      return (this.config.contextTabs || []).find((item) => item.handle === "ctx-color")?.params?.[0];
    }

    // After a reload or Back, brand results still narrowed to the product's colour keep following it.
    restoreDefaultColor(url) {
      const color = this.colorParam();
      this.defaultColor =
        this.contextApplied &&
        color &&
        this.activeHandle === "ctx-brand" &&
        url.searchParams.getAll(color[0]).includes(color[1])
          ? { handle: this.activeHandle, key: color[0], value: color[1] }
          : null;
    }

    // Too few products in that colour: show the whole brand instead.
    checkDefaultColor() {
      if (!this.defaultColor) return;
      const { handle, key, value } = this.defaultColor;
      const url = new URL(window.location.href);
      const values = url.searchParams.getAll(key);
      if (this.activeHandle !== handle || !values.includes(value)) {
        this.defaultColor = null;
        return;
      }
      if (this.panel.querySelectorAll("#PdpBrowseLoop > *").length >= DEFAULT_COLOR_MIN_RESULTS)
        return;
      this.defaultColor = null;
      url.searchParams.delete(key);
      values
        .filter((item) => item !== value)
        .forEach((item) => url.searchParams.append(key, item));
      history.replaceState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true, preserveScroll: true });
    }

    disconnectedCallback() {
      this.disconnectAbObserver();
      this.disconnectContextObserver();
      this.removeEventListener("click", this.onClick);
      this.removeEventListener("keydown", this.onKeyDown);
      this.removeEventListener("submit", this.onSubmit);
      this.removeEventListener("input", this.onInput);
      this.removeEventListener("change", this.onChange);
      this.removeEventListener("theme:range:update", this.onRangeUpdate);
      window.removeEventListener("popstate", this.onPopState);
      document.removeEventListener("theme:variant:change", this.onVariantChange);
      if (this.tablist) {
        this.tablist.removeEventListener("pointerdown", this.onTabPointerDown);
        this.tablist.removeEventListener("pointermove", this.onTabPointerMove);
        this.tablist.removeEventListener("pointerup", this.onTabPointerUp);
        this.tablist.removeEventListener("pointercancel", this.onTabPointerUp);
      }
      if (this.abortController) this.abortController.abort();
    }

    resolveActiveHandle(url) {
      const requested = url.searchParams.get("browse");
      const match = this.config.tabs.find((tab) => tab.handle === requested);
      if (match) return match.handle;
      if (this.config.activeHandle) return this.config.activeHandle;
      const tabs = this.config.tabs || [];
      if (!tabs.length) return "";
      return tabs.reduce(
        (best, tab) => ((tab.depth || 0) > (best.depth || 0) ? tab : best),
        tabs[0],
      ).handle;
    }

    getTab(handle) {
      return (
        this.config.tabs.find((tab) => tab.handle === handle) ||
        this.config.tabs[0]
      );
    }

    productUrl(handle, searchParams, page) {
      const url = new URL(this.config.productUrl, window.location.origin);
      const current = new URL(window.location.href);
      current.searchParams.forEach((value, key) => {
        if (!isManagedParam(key)) url.searchParams.set(key, value);
      });
      url.searchParams.set("browse", handle);
      if (searchParams) {
        searchParams.forEach((value, key) => {
          if (key === "sort_by" || key.startsWith(FILTER_PREFIX)) {
            url.searchParams.append(key, value);
          }
        });
      }
      if (page && page > 1) {
        url.searchParams.set("page", String(page));
      }
      return url;
    }

    collectionFetchUrl(handle, searchParams, page) {
      const tab = this.getTab(handle);
      const url = new URL(tab.url, window.location.origin);
      if (searchParams) {
        searchParams.forEach((value, key) => {
          if (key === "sort_by" || key.startsWith(FILTER_PREFIX)) {
            url.searchParams.append(key, value);
          }
        });
      }
      if (page && page > 1) url.searchParams.set("page", String(page));
      url.searchParams.set("section_id", this.config.resultsSectionId);
      url.searchParams.set("pdp_grid", String(this.config.settings.grid));
      url.searchParams.set(
        "pdp_grid_mobile",
        String(this.config.settings.gridMobile),
      );
      url.searchParams.set(
        "pdp_per_page",
        String(this.config.settings.productsPerPage),
      );
      url.searchParams.set(
        "pdp_filter_layout",
        this.config.settings.filterLayout,
      );
      url.searchParams.set(
        "pdp_enable_sort",
        String(this.config.settings.enableSort),
      );
      url.searchParams.set(
        "pdp_enable_filters",
        String(this.config.settings.enableFilters),
      );
      return url;
    }

    paramsFromProductUrl(url) {
      const params = new URLSearchParams();
      url.searchParams.forEach((value, key) => {
        if (key === "sort_by" || key.startsWith(FILTER_PREFIX))
          params.append(key, value);
      });
      return params;
    }

    paramsFromForm() {
      const form =
        this.panel &&
        this.panel.querySelector("[data-collection-filters-form]");
      const params = new URLSearchParams();
      if (!form) return params;

      const formData = new FormData(form);
      const rangeMin = form.querySelector("[data-se-min]");
      const rangeMax = form.querySelector("[data-se-max]");
      const minLimit = rangeMin ? rangeMin.getAttribute("data-se-min") : "";
      const maxLimit = rangeMax ? rangeMax.getAttribute("data-se-max") : "";
      let defaultPriceCount = 0;

      formData.forEach((value, key) => {
        if (!value) return;
        if (key.startsWith(FILTER_PREFIX) || key === "sort_by") {
          params.append(key, value);
          if (
            (key === "filter.v.price.gte" &&
              String(value) === String(minLimit)) ||
            (key === "filter.v.price.lte" && String(value) === String(maxLimit))
          ) {
            defaultPriceCount += 1;
          }
        }
      });

      if (defaultPriceCount === 2) {
        params.delete("filter.v.price.gte");
        params.delete("filter.v.price.lte");
      }

      const sortInput = this.panel.querySelector(
        '[data-popout-input][name="sort_by"]',
      );
      if (sortInput && sortInput.value && !params.has("sort_by")) {
        params.set("sort_by", sortInput.value);
      }

      return params;
    }

    handleClick(event) {
      const tab = event.target.closest("[data-browse-handle]");
      if (tab && this.contains(tab)) {
        event.preventDefault();
        if (this.suppressTabClick) {
          this.suppressTabClick = false;
          return;
        }
        this.selectTab(tab.getAttribute("data-browse-handle"));
        return;
      }

      const filterLink = event.target.closest("[data-filter-update-url]");
      if (filterLink && this.panel && this.panel.contains(filterLink)) {
        event.preventDefault();
        this.applyCollectionHref(filterLink.getAttribute("href"));
        return;
      }

      const sortLink = event.target.closest("[data-sort-link]");
      if (sortLink && this.panel && this.panel.contains(sortLink)) {
        event.preventDefault();
        const sortInput = this.panel.querySelector(
          '[data-popout-input][name="sort_by"]',
        );
        if (sortInput) {
          sortInput.value = sortLink.getAttribute("data-value") || "";
          sortInput.disabled = false;
        }
        this.applyCurrentForm();
        return;
      }

      const showMore = event.target.closest("[data-show-more]");
      if (showMore && this.panel && this.panel.contains(showMore)) {
        event.preventDefault();
        this.expandShowMore(showMore);
        return;
      }

      const toggle = event.target.closest("[data-aria-toggle]");
      if (toggle && this.panel && this.panel.contains(toggle)) {
        event.preventDefault();
        this.toggleFilters(toggle);
        return;
      }

      const closer = event.target.closest("[data-collection-sidebar-close]");
      if (closer && this.panel && this.panel.contains(closer)) {
        event.preventDefault();
        this.hideFilters();
        return;
      }

      const loadMore = event.target.closest("[data-pdp-load-more]");
      if (loadMore && this.panel && this.panel.contains(loadMore)) {
        event.preventDefault();
        this.loadMore(loadMore);
      }
    }

    handleTabPointerDown(event) {
      if (
        !this.tablist ||
        window.innerWidth < 992 ||
        event.button !== 0 ||
        event.pointerType === "touch"
      ) {
        return;
      }

      this.tabDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        scrollLeft: this.tablist.scrollLeft,
        moved: false,
      };
      this.tablist.classList.add("is-dragging");
    }

    handleTabPointerMove(event) {
      if (!this.tablist || !this.tabDrag) return;
      if (event.pointerId !== this.tabDrag.pointerId) return;

      // The button was let go outside the row before a drag started.
      if (!(event.buttons & 1)) {
        this.tablist.classList.remove("is-dragging");
        this.tabDrag = null;
        return;
      }

      const distance = event.clientX - this.tabDrag.startX;
      if (!this.tabDrag.moved && Math.abs(distance) < 5) return;

      // Capture only once a real drag starts. Capturing on pointerdown sends
      // the click to the row instead of the tab, so a plain click did nothing.
      if (!this.tabDrag.moved) this.tablist.setPointerCapture(event.pointerId);

      this.tabDrag.moved = true;
      event.preventDefault();
      this.tablist.scrollLeft = this.tabDrag.scrollLeft - distance;
    }

    handleTabPointerUp(event) {
      if (!this.tablist || !this.tabDrag) return;
      if (event.pointerId !== this.tabDrag.pointerId) return;

      const moved = this.tabDrag.moved;
      if (this.tablist.hasPointerCapture(event.pointerId)) {
        this.tablist.releasePointerCapture(event.pointerId);
      }
      this.tablist.classList.remove("is-dragging");
      this.tabDrag = null;

      if (moved) {
        this.suppressTabClick = true;
        this.snapTablist();
        requestAnimationFrame(() => {
          this.suppressTabClick = false;
        });
      }
    }

    snapTablist() {
      if (!this.tablist) return;

      const tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      const currentScroll = this.tablist.scrollLeft;
      const maxScroll = Math.max(
        0,
        this.tablist.scrollWidth - this.tablist.clientWidth,
      );
      const listLeft = this.tablist.getBoundingClientRect().left;
      let targetScroll = currentScroll;
      let closestDistance = Number.POSITIVE_INFINITY;

      tabs.forEach((tab) => {
        const tabOffset =
          currentScroll + tab.getBoundingClientRect().left - listLeft;
        const candidate = Math.max(0, Math.min(tabOffset, maxScroll));
        const distance = Math.abs(candidate - currentScroll);

        if (distance <= closestDistance) {
          closestDistance = distance;
          targetScroll = candidate;
        }
      });

      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      this.tablist.scrollTo({
        left: targetScroll,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    }

    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.hideFilters();
        return;
      }

      if (!this.tablist || !this.tablist.contains(event.target)) return;
      const tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
      const index = tabs.indexOf(event.target);
      if (index < 0) return;

      let nextIndex = -1;
      if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
      if (event.key === "ArrowLeft")
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === "Home") nextIndex = 0;
      if (event.key === "End") nextIndex = tabs.length - 1;
      if (nextIndex < 0) return;

      event.preventDefault();
      tabs[nextIndex].focus();
      this.selectTab(tabs[nextIndex].getAttribute("data-browse-handle"));
    }

    handleSubmit(event) {
      if (!this.panel || !this.panel.contains(event.target)) return;
      event.preventDefault();
      this.applyCurrentForm();
    }

    handleInput(event) {
      if (!this.panel || !this.panel.contains(event.target)) return;
      const target = event.target;
      if (!target.matches("[data-field-price-min], [data-field-price-max]"))
        return;
      const minInput = this.panel.querySelector("[data-field-price-min]");
      const maxInput = this.panel.querySelector("[data-field-price-max]");
      if (
        target.hasAttribute("data-field-price-min") &&
        maxInput &&
        !maxInput.value
      ) {
        maxInput.value = maxInput.placeholder;
      }
      if (
        target.hasAttribute("data-field-price-max") &&
        minInput &&
        !minInput.value
      ) {
        minInput.value = minInput.placeholder;
      }
      this.applyCurrentForm();
    }

    handleChange(event) {
      if (!this.panel || !this.panel.contains(event.target)) return;
      const target = event.target;
      if (target.matches('input[type="checkbox"], input[type="radio"]')) {
        this.applyCurrentForm();
      }
    }

    handleRangeUpdate(event) {
      if (!this.panel || !this.panel.contains(event.target)) return;
      this.applyCurrentForm();
    }

    handlePopState() {
      const url = new URL(window.location.href);
      this.activeHandle = this.resolveActiveHandle(url);
      this.restoreDefaultColor(url);
      this.syncTabs(this.activeHandle);
      this.loadPanel({ replace: true, push: false });
    }

    selectTab(handle) {
      if (!handle || (handle === this.activeHandle && !this.defaultColor)) {
        this.syncTabs(handle);
        return;
      }
      // Clicking the brand tab while its first results are narrowed to the colour opens the whole brand.
      this.defaultColor = null;
      this.activeHandle = handle;
      this.syncTabs(handle);
      const url = this.productUrl(handle, this.tabParams(handle));
      history.pushState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true });
    }

    applyCurrentForm() {
      const params = this.paramsFromForm();
      const url = this.productUrl(this.activeHandle, params);
      history.pushState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true });
    }

    applyCollectionHref(href) {
      if (!href) return;
      const collectionUrl = new URL(href, window.location.origin);
      SETTING_KEYS.forEach((key) => collectionUrl.searchParams.delete(key));
      collectionUrl.searchParams.delete("section_id");
      const params = new URLSearchParams();
      collectionUrl.searchParams.forEach((value, key) => {
        if (key === "sort_by" || key.startsWith(FILTER_PREFIX))
          params.append(key, value);
      });
      const url = this.productUrl(this.activeHandle, params);
      history.pushState({}, "", url.pathname + url.search);
      this.loadPanel({ replace: true });
    }

    syncTabs(handle) {
      if (!this.tablist) return;
      this.tablist.querySelectorAll('[role="tab"]').forEach((tab) => {
        const selected = tab.getAttribute("data-browse-handle") === handle;
        tab.classList.toggle("is-active", selected);
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.tabIndex = selected ? 0 : -1;
      });
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || "";
    }

    setBusy(isBusy) {
      if (!this.panel) return;
      this.panel.setAttribute("aria-busy", isBusy ? "true" : "false");
    }

    async loadPanel({
      replace = true,
      push = false,
      preserveScroll = false,
    } = {}) {
      if (!this.panel || !this.activeHandle) return;
      const currentUrl = new URL(window.location.href);
      const handle = this.resolveActiveHandle(currentUrl);
      this.activeHandle = handle;
      const params = this.paramsFromProductUrl(currentUrl);
      const fetchUrl = this.collectionFetchUrl(handle, params, 1);

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const requestId = ++this.requestId;

      this.setBusy(true);
      this.setStatus(this.config.strings.loading);

      try {
        const response = await fetch(fetchUrl.pathname + fetchUrl.search, {
          headers: { Accept: "text/html" },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error("HTTP " + response.status);
        const html = await response.text();
        if (requestId !== this.requestId) return;

        const doc = new DOMParser().parseFromString(html, "text/html");
        const incoming = doc.querySelector("[data-pdp-collection-results]");
        if (!incoming) throw new Error("Missing results section");

        const scrollY = preserveScroll ? window.scrollY : null;
        const wrapper =
          incoming.closest(".shopify-section") || incoming.parentElement;
        const nodes = wrapper ? Array.from(wrapper.childNodes) : [incoming];
        this.panel.replaceChildren(...nodes);
        this.setBusy(false);
        this.setStatus("");
        this.afterInject();
        this.showColorPhotos(fetchUrl.searchParams, this.panel);

        if (scrollY !== null) {
          window.scrollTo({ top: scrollY, left: 0, behavior: "instant" });
        } else if (replace && !preserveScroll) {
          this.scrollToPanel();
        }

        if (push) {
          const next = this.productUrl(handle, params);
          history.pushState({}, "", next.pathname + next.search);
        }
      } catch (error) {
        if (error && error.name === "AbortError") return;
        console.error("[pdp-collection-browser] loadPanel", error);
        this.renderError();
      }
    }

    afterInject() {
      if (this.contextApplied) this.classList.add("is-context-ready");
      const grid = this.panel.querySelector("[data-products-grid]");
      if (grid) grid.classList.remove("is-loading");
      this.checkDefaultColor();
    }

    // Results narrowed to one colour show each product's photo in that colour instead of its main photo
    // (Shopify already links each card to that colour).
    showColorPhotos(params, root) {
      if (!this.contextApplied || !root) return;
      const colors = params.getAll(COLOR_FILTER);
      if (colors.length !== 1) return;
      const color = colors[0].toLowerCase();

      root.querySelectorAll("[data-product-image-default]").forEach((holder) => {
        const photo = holder.querySelector(":scope > figure img");
        const match = Array.from(
          holder.querySelectorAll(".product-item__bg__inner[data-variant-title]"),
        ).find((inner) =>
          inner.dataset.variantTitle
            .split(" / ")
            .some((value) => value.trim().toLowerCase() === color),
        );
        const source = match?.querySelector("img");
        if (!photo || !source) return;
        photo.src = source.getAttribute("src");
        if (source.hasAttribute("srcset"))
          photo.srcset = source.getAttribute("srcset");
        this.showColorSlideFirst(holder.parentElement, source);
      });
    }

    // On touch screens the card's swipeable photos sit over the main photo, so they start with the colour photo too.
    // The photos swap places rather than the slides: moving a slide makes the strip snap back to the old first photo.
    showColorSlideFirst(link, source) {
      if (!window.theme?.touch) return;
      const slider = link?.querySelector("[data-hover-slider]");
      const slides = slider
        ? Array.from(slider.querySelectorAll(":scope > [data-hover-slide-touch]"))
        : [];
      const first = slides[0]?.querySelector("img");
      if (!first) return;

      const file = (img) => (img?.getAttribute("src") || "").split("/").pop().split("?")[0];
      if (file(first) === file(source)) return;

      const copyPhoto = (from, to) =>
        ["src", "srcset"].forEach((attr) => {
          const value = from.getAttribute(attr);
          if (value === null) to.removeAttribute(attr);
          else to.setAttribute(attr, value);
        });

      const match = slides
        .map((item) => item.querySelector("img"))
        .find((img) => img && file(img) === file(source));
      if (match) {
        const firstPhoto = first.cloneNode();
        copyPhoto(match, first);
        copyPhoto(firstPhoto, match);
        return;
      }

      // The colour photo isn't among the slides: it goes first and the old first photo moves to the end.
      slider.append(slides[0].cloneNode(true));
      copyPhoto(source, first);
      slider
        .closest("hover-images")
        ?.style.setProperty("--slides-count", slides.length + 1);
    }

    async loadMore(button) {
      const nextUrl = button.getAttribute("data-url");
      if (!nextUrl || button.disabled) return;

      const text = button.querySelector("[data-pdp-load-more-text]");
      button.disabled = true;
      if (text) text.hidden = true;
      this.setStatus(this.config.strings.loading);

      try {
        const source = new URL(nextUrl, window.location.origin);
        const page = parseInt(source.searchParams.get("page") || "2", 10);
        let params = new URLSearchParams();
        source.searchParams.forEach((value, key) => {
          if (key === "sort_by" || key.startsWith(FILTER_PREFIX))
            params.append(key, value);
        });
        if (![...params.keys()].length) {
          params = this.paramsFromProductUrl(new URL(window.location.href));
        }

        const fetchUrl = this.collectionFetchUrl(
          this.activeHandle,
          params,
          page,
        );
        const response = await fetch(fetchUrl.pathname + fetchUrl.search, {
          headers: { Accept: "text/html" },
        });
        if (!response.ok) throw new Error("HTTP " + response.status);

        const doc = new DOMParser().parseFromString(
          await response.text(),
          "text/html",
        );
        const grid = this.panel.querySelector("#PdpBrowseLoop");
        const newGrid = doc.querySelector("#PdpBrowseLoop");
        if (!grid || !grid.isConnected || !newGrid)
          throw new Error("Grid not found");

        Array.from(newGrid.children).forEach((child) =>
          grid.appendChild(child),
        );
        this.showColorPhotos(fetchUrl.searchParams, grid);

        const nextButton = doc.querySelector("[data-pdp-load-more]");
        const nextHref = nextButton && nextButton.getAttribute("data-url");
        if (nextHref) {
          button.setAttribute("data-url", nextHref);
          button.disabled = false;
          if (text) text.hidden = false;
        } else {
          const wrap = button.closest(".pdp-collection-browser__more");
          if (wrap) wrap.remove();
        }
        this.setStatus("");
      } catch (error) {
        console.error("[pdp-collection-browser] loadMore", error);
        button.disabled = false;
        if (text) text.hidden = false;
        this.setStatus(this.config.strings.error);
      }
    }

    renderError() {
      if (this.contextApplied) this.classList.add("is-context-ready");
      this.setBusy(false);
      this.setStatus(this.config.strings.error);
      const error = document.createElement("div");
      error.className = "pdp-collection-browser__error";
      const message = document.createElement("p");
      message.textContent = this.config.strings.error;
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "btn btn--primary btn--outline";
      retry.textContent = this.config.strings.retry;
      retry.addEventListener("click", () =>
        this.loadPanel({ replace: true, preserveScroll: true }),
      );
      error.append(message, retry);
      this.panel.replaceChildren(error);
    }

    expandShowMore(button) {
      const actions = button.parentElement;
      if (actions) actions.classList.add("hidden");
      const group = actions && actions.previousElementSibling;
      if (!group) return;
      group
        .querySelectorAll("[data-link-hidden]")
        .forEach((item) => item.classList.remove("hidden"));
    }

    toggleFilters(button) {
      const expanded = button.getAttribute("aria-expanded") === "true";
      button.setAttribute("aria-expanded", expanded ? "false" : "true");
      const sidebar = this.panel.querySelector("[data-collection-sidebar]");
      if (!sidebar) return;
      sidebar.classList.toggle("expanded", !expanded);
      sidebar.classList.add("drawer--animated");
      if (
        !expanded &&
        (isMobileView() ||
          sidebar.matches(
            "[data-collection-sidebar-slide-out], .collection__sidebar__slide-out",
          ))
      ) {
        document.dispatchEvent(
          new CustomEvent("theme:scroll:lock", { bubbles: true }),
        );
      } else {
        document.dispatchEvent(
          new CustomEvent("theme:scroll:unlock", { bubbles: true }),
        );
      }
    }

    hideFilters() {
      const button =
        this.panel && this.panel.querySelector("[data-aria-toggle]");
      const sidebar =
        this.panel && this.panel.querySelector("[data-collection-sidebar]");
      if (button) button.setAttribute("aria-expanded", "false");
      if (sidebar) sidebar.classList.remove("expanded");
      document.dispatchEvent(
        new CustomEvent("theme:scroll:unlock", { bubbles: true }),
      );
      if (button && document.body.classList.contains("is-focused"))
        button.focus();
    }

    scrollToPanel() {
      const nav =
        this.panel.querySelector("[data-collection-nav]") ||
        this.tablist ||
        this.panel;
      const top = nav.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: "smooth" });
    }
  }

  if (!customElements.get("pdp-collection-browser")) {
    customElements.define("pdp-collection-browser", PdpCollectionBrowser);
  }
})();
